import {
  CUSTOMER_EMAIL_WAITS,
  CUSTOMER_EMAIL_WAIT_LINE,
  dailyLimitWaitLine,
  emailNoticeLine,
  ownerUnconfirmedLine,
  type CustomerEmailWait,
  type EmailNotice,
  type EmailNoticeKind,
  type EmailStatus,
  type SendingMode,
} from '@servwell/shared';
import { REPLY_ADDRESS_ANCHOR } from '../tickets/ticketView';
import type { Tone } from '../ui/Badge';

/**
 * What Settings → Email and the app-wide email banner say (MAP 249 spec §9, ticket 07), pure so
 * each state is a test rather than a render. Neither screen decides anything: the server's
 * `email.status` says what is true (the route, today's count, what waits, which notices apply);
 * this turns it into words, a tone, and the exact control each notice's link lands on.
 */

/**
 * The card's anchors. `email` is the card (`/settings#email`); the rest are the controls inside
 * it a link can land on (each one scrolled to and marked, `useLanding`). Named after what they
 * hold, and promises once shipped (`settings-anchors.test.ts`): `customer-replies` is the ticket
 * composer's, `asked-not-to-hear` was the suppression card's own before it moved in here.
 */
export const EMAIL_ANCHOR = {
  card: 'email',
  status: 'email-status',
  sendingFrom: 'email-sending-from',
  /** "Your domain", under Sending from (ticket 08): where its banner and its owner email land. */
  domain: 'email-domain',
  today: 'email-today',
  customerReplies: REPLY_ADDRESS_ANCHOR,
  automaticEmails: 'automatic-emails',
  reviewLink: 'review-link',
  askedNotToHear: 'asked-not-to-hear',
} as const;

/** Where each notice's link lands: the control that answers it. */
export const NOTICE_ANCHOR: Record<EmailNoticeKind, string> = {
  paused: EMAIL_ANCHOR.status,
  held: EMAIL_ANCHOR.status,
  // The reply address's control; a From name's is the domain's (`noticeView`).
  sender_held: EMAIL_ANCHOR.customerReplies,
  owner_unconfirmed: EMAIL_ANCHOR.status,
  domain_failing: EMAIL_ANCHOR.domain,
  mailbox_reconnect: EMAIL_ANCHOR.sendingFrom,
  content_review: EMAIL_ANCHOR.today,
  reply_address: EMAIL_ANCHOR.customerReplies,
  daily_limit: EMAIL_ANCHOR.today,
  route_limit: EMAIL_ANCHOR.today,
};

/** Red when nothing goes (or goes the wrong way); amber when some emails wait. */
export const NOTICE_TONE: Record<EmailNoticeKind, Tone> = {
  paused: 'danger',
  held: 'danger',
  sender_held: 'danger',
  owner_unconfirmed: 'warn',
  domain_failing: 'danger',
  mailbox_reconnect: 'danger',
  content_review: 'warn',
  reply_address: 'warn',
  daily_limit: 'warn',
  route_limit: 'warn',
};

/** What a notice's link says (the place it opens names the fix). */
const NOTICE_LINK: Record<EmailNoticeKind, string> = {
  paused: 'Email settings',
  held: 'Email settings',
  sender_held: 'Change it',
  owner_unconfirmed: 'Email settings',
  domain_failing: 'Check your domain',
  mailbox_reconnect: 'Reconnect',
  content_review: 'See what’s waiting',
  reply_address: 'Reply address',
  daily_limit: 'See today’s emails',
  route_limit: 'See today’s emails',
};

/** Can the person reading confirm the shop's owner email themselves (an unconfirmed owner)? */
export function canResendConfirmation(s: Pick<EmailStatus, 'you'>): boolean {
  return s.you.owner && !s.you.confirmed;
}

/** One notice as a screen shows it: the sentence, its tone, and what to do about it. */
export interface NoticeView {
  kind: EmailNoticeKind;
  tone: Tone;
  text: string;
  /**
   * "Send it again" (`me.resendEmailConfirmation`) in place of a link: the unconfirmed owner
   * fixes it from where they stand.
   */
  resend: boolean;
  link: { label: string; hash: string };
}

export function noticeView(n: EmailNotice, s: Pick<EmailStatus, 'you'>): NoticeView {
  const resend = n.kind === 'owner_unconfirmed' && canResendConfirmation(s);
  return {
    kind: n.kind,
    tone: NOTICE_TONE[n.kind],
    text: resend ? ownerUnconfirmedLine(s.you.email) : emailNoticeLine(n),
    resend,
    link: {
      label: NOTICE_LINK[n.kind],
      hash:
        n.kind === 'sender_held' && n.what === 'from_name'
          ? EMAIL_ANCHOR.domain
          : NOTICE_ANCHOR[n.kind],
    },
  };
}

/** "Today: 12 of 50 sent · 3 waiting": the route's count, and everything that waits. */
export function todayLine(s: Pick<EmailStatus, 'today' | 'waiting'>): string {
  const waiting = waitingTotal(s.waiting);
  return `Today: ${s.today.sent} of ${s.today.cap} sent · ${waiting} waiting`;
}

export function waitingTotal(waiting: EmailStatus['waiting']): number {
  return Object.values(waiting).reduce((sum, n) => sum + (n ?? 0), 0);
}

/** What waits and why, in the outbox's own words, in the waits' order (only reasons with any). */
export function waitingRows(
  s: Pick<EmailStatus, 'waiting' | 'notices'>,
): { wait: CustomerEmailWait; count: number; line: string }[] {
  return CUSTOMER_EMAIL_WAITS.filter((w) => (s.waiting[w] ?? 0) > 0).map((w) => ({
    wait: w,
    count: s.waiting[w]!,
    line:
      w === 'daily_limit' ? dailyLimitRowLine(s.notices) : CUSTOMER_EMAIL_WAIT_LINE[w],
  }));
}

/**
 * The daily limit's row states what the server read (the first waiting row's own schedule, or
 * that it is due for retry), never a day or time of its own making (Codex 07-r1 #2, r2, r3).
 * Without a notice (email off), a line with no time at all.
 */
function dailyLimitRowLine(notices: EmailStatus['notices']): string {
  const n = notices.find(
    (x): x is Extract<EmailNotice, { kind: 'daily_limit' }> => x.kind === 'daily_limit',
  );
  return n ? dailyLimitWaitLine(n) : 'Waiting for a daily email limit';
}

/** The three ways to send, as "Sending from" lists them. */
export const SENDING_MODE_LABEL: Record<SendingMode, string> = {
  servwell: 'ServWell’s address',
  domain: 'Your domain',
  mailbox: 'Your mailbox',
};

/** Where one route stands, in a short phrase beside its name. */
export function routeState(
  mode: SendingMode,
  s: Pick<EmailStatus, 'domain' | 'mailbox' | 'ready'>,
): string {
  switch (mode) {
    case 'servwell':
      return 'Always available';
    case 'domain':
      if (!s.domain) return 'Not set up';
      if (s.domain.held) return `${s.domain.domain}: waiting for ServWell’s check`;
      if (s.domain.failingSince) return `${s.domain.domain}: stopped working`;
      return s.domain.verified
        ? `${s.domain.domain}: verified`
        : `${s.domain.domain}: waiting for its records`;
    case 'mailbox':
      if (!s.mailbox) return 'Not available yet';
      return s.mailbox.status === 'needs_reconnect'
        ? `${s.mailbox.address}: needs to be connected again`
        : `${s.mailbox.address}: connected`;
  }
}

/**
 * The line under "Sending from" when the shop's choice is not what its emails go by: the chosen
 * route stopped working, so they go from ServWell's address meanwhile (spec §7, §8).
 */
export function fallbackLine(
  s: Pick<EmailStatus, 'sendingMode' | 'route'>,
): string | null {
  if (s.sendingMode === s.route) return null;
  return `Your emails go from ServWell’s address until ${
    s.sendingMode === 'domain' ? 'your domain' : 'your mailbox'
  } works again.`;
}
